import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Book, Film, Lightbulb, DollarSign, Calendar, ArrowRight, Bell } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format, isAfter, subDays } from 'date-fns';

const WhatsNewSection = ({ isMobile = false, userRole = 'kid' }) => {
  const { ideas, kidAllowances, currentUser, familyUsers } = useFamilyContext();
  const [newItems, setNewItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const gatherNewItems = async () => {
      if (!currentUser?.id) return;
      
      try {
        const sevenDaysAgo = subDays(new Date(), 7);
        const items = [];

        // Get new ideas (books, movies, projects, etc.)
        if (ideas && ideas.length > 0) {
          const newIdeas = ideas.filter(idea => {
            const createdAt = new Date(idea.created_at);
            return isAfter(createdAt, sevenDaysAgo);
          });

          newIdeas.forEach(idea => {
            items.push({
              id: `idea-${idea.id}`,
              type: 'idea',
              category: idea.category || 'general',
              title: idea.title || idea.name,
              description: idea.description,
              createdAt: new Date(idea.created_at),
              link: '/ideas',
              icon: getIdeaIcon(idea.category)
            });
          });
        }

        // Get new allowance activities (for kids)
        if (userRole === 'kid' && kidAllowances && kidAllowances[currentUser.id]) {
          const allowanceData = kidAllowances[currentUser.id];
          if (allowanceData.recentActivity) {
            const newAllowanceItems = allowanceData.recentActivity.filter(activity => {
              const activityDate = new Date(activity.date || activity.created_at);
              return isAfter(activityDate, sevenDaysAgo);
            });

            newAllowanceItems.forEach(activity => {
              items.push({
                id: `allowance-${activity.id}`,
                type: 'allowance',
                title: activity.type === 'job_completion' ? 'Job Completed!' : 'Allowance Update',
                description: activity.description || `${activity.type === 'addition' ? '+' : ''}$${activity.amount}`,
                createdAt: new Date(activity.date || activity.created_at),
                link: '/allowance',
                icon: DollarSign,
                amount: activity.amount
              });
            });
          }
        }

        // Sort by creation date (newest first)
        items.sort((a, b) => b.createdAt - a.createdAt);

        setNewItems(items.slice(0, 5)); // Show max 5 items
      } catch (error) {
        console.error('Error gathering new items:', error);
      } finally {
        setLoading(false);
      }
    };

    gatherNewItems();
  }, [ideas, kidAllowances, currentUser?.id, userRole]);

  const getIdeaIcon = (category) => {
    switch (category) {
      case 'book':
      case 'books':
        return Book;
      case 'movie':
      case 'movies':
        return Film;
      case 'project':
      case 'projects':
        return Lightbulb;
      default:
        return Sparkles;
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return format(date, 'MMM d');
  };

  if (loading) {
    return (
      <div className={`bg-gradient-to-r from-azure-50 to-violet-50 border border-azure-200 rounded-xl p-4 ${isMobile ? '' : 'h-full'}`}>
        <div className="animate-pulse">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 bg-azure-200 rounded"></div>
            <div className="h-4 bg-azure-200 rounded w-24"></div>
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-azure-200 rounded"></div>
            <div className="h-3 bg-azure-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (newItems.length === 0) {
    return null; // Don't show section if no new items
  }

  return (
    <div className={`bg-gradient-to-r from-azure-50 to-violet-50 border border-azure-200 rounded-xl p-4 ${isMobile ? '' : 'h-full'}`}>
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-5 h-5 text-azure-600" />
        <h3 className="font-semibold text-azure-900">What's New</h3>
        <span className="bg-azure-600 text-white text-xs px-2 py-1 rounded-full">
          {newItems.length}
        </span>
      </div>

      <div className="space-y-3">
        {newItems.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.id} className="flex items-start gap-3 p-2 bg-white/60 rounded-lg hover:bg-white/80 transition-colors">
              <div className="w-8 h-8 bg-azure-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-azure-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-azure-900 text-sm truncate">
                    {item.title}
                  </h4>
                  <span className="bg-emerald-100 text-emerald-800 text-xs px-1.5 py-0.5 rounded-full font-medium">
                    NEW
                  </span>
                </div>
                <p className="text-xs text-azure-700 mb-1 line-clamp-2">
                  {item.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-azure-600">
                    {formatTimeAgo(item.createdAt)}
                  </span>
                  {item.amount && (
                    <span className="text-xs font-medium text-emerald-600">
                      ${item.amount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {newItems.length > 0 && (
          <div className="text-center pt-2">
            <Link
              to={newItems[0].link}
              className="inline-flex items-center gap-2 text-sm font-medium text-azure-700 hover:text-azure-800 transition-colors"
            >
              View All Updates
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsNewSection;